// src/components/Sidebar.jsx
import React from "react";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import {
  SettingsOutlined,
  ChevronLeft,
  ChevronRightOutlined,
  HomeOutlined,
  Groups2Outlined,
  ReceiptLongOutlined,
  AdminPanelSettingsOutlined,
  SupervisorAccountOutlined,
  TrendingUpOutlined,
} from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FlexBetween from "./FlexBetween";
import profileImage from "assets/profile.jpeg";
import AccountBalanceWalletOutlined from "@mui/icons-material/AccountBalanceWalletOutlined";
import SwapHorizOutlined from "@mui/icons-material/SwapHorizOutlined";
import VolunteerActivismOutlined from "@mui/icons-material/VolunteerActivismOutlined";
import CreditScoreOutlined from "@mui/icons-material/CreditScoreOutlined";
import VerifiedUserOutlined from "@mui/icons-material/VerifiedUserOutlined";
import GavelOutlined from "@mui/icons-material/GavelOutlined";
import AssignmentLateOutlined from "@mui/icons-material/AssignmentLateOutlined";
import ReportProblemOutlined from "@mui/icons-material/ReportProblemOutlined";
import CampaignOutlined from "@mui/icons-material/CampaignOutlined";

// ✅ CHANGE 1: Add a 'path' property to each navigable item.
const navItems = [
  {
    text: "Dashboard",
    icon: <HomeOutlined />,
    path: "/dashboard",
  },
  {
    text: "Groups",
    icon: <Groups2Outlined />,
    path: "/groups",
    sysAdminOnly: true, // ✅ hide unless roleGroupId === 1
  },

  {
    text: "My Finances",
    icon: null,
  },
  {
    text: "My Transactions",
    icon: <SwapHorizOutlined />,
    path: "/transactions/my",
  },
  {
    text: "My Contributions",
    icon: <VolunteerActivismOutlined />,
    path: "/contributions/my-schedule",
  },
  {
    text: "Funds Drives",
    icon: <CampaignOutlined />,
    path: "/funds-drives",
  },
  {
    text: "My Loans",
    icon: <CreditScoreOutlined />,
    path: "/loans/my",
  },
  {
    text: "My Guarantees",
    icon: <VerifiedUserOutlined />,
    path: "/loans/guarantees",
  },
  {
    text: "My Fines",
    icon: <AssignmentLateOutlined />,
    path: "/fines/my",
  },
  {
    text: "Chamaa 360",
    icon: null,
  },
  {
    text: "Group Transactions",
    icon: <Groups2Outlined />,
    path: "/transactions/group",
  },
  {
    text: "All Loans",
    icon: <TrendingUpOutlined />,
    path: "/loans/all",
  },
  {
    text: "Members",
    icon: <Groups2Outlined />,
    path: "/users",
  },
  {
    text: "Group Positions",
    icon: <AdminPanelSettingsOutlined />,
    path: "/group-positions",
  },
  {
    text: "Group Officials",
    icon: <SupervisorAccountOutlined />,
    path: "/group-officials",
  },
  {
    text: "Group Fines",
    icon: <ReportProblemOutlined />,
    path: "/fines/group",
  },
  {
    text: "Fine Types",
    icon: <GavelOutlined />,
    path: "/fines/types",
  },
  {
    text: "Contribution Policy",
    icon: <VolunteerActivismOutlined />,
    path: "/contributions/policy",
  },  
  {
    text: "Role Management",
    icon: null,
    sysAdminOnly: true,
  },
  {
    text: "Admin",
    icon: <AdminPanelSettingsOutlined />,
    path: "/admin",
    sysAdminOnly: true,
  },
  {
    text: "Performance",
    icon: <TrendingUpOutlined />,
    path: "/performance",
    sysAdminOnly: true,
  },
];

const Sidebar = ({
  user,
  drawerWidth,
  isSidebarOpen,
  setIsSidebarOpen,
  isNonMobile,
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  const [active, setActive] = useState("");

  // ✅ Force a re-render when group changes (same tab + other tabs)
  const [, forceRefresh] = useState(0);

  // ✅ RoleGroup + GroupName are read from localStorage so they always reflect latest token
  const roleGroupId = localStorage.getItem("roleGroupId");
  const isSysAdmin = roleGroupId === "1";

  // ✅ IMPORTANT: don't cache groupName in state once.
  // Read it directly so it always reflects localStorage (after re-render).
  const groupName = localStorage.getItem("groupName") || "";

  useEffect(() => {
    setActive(pathname.substring(1));
  }, [pathname]);

  useEffect(() => {
    // Other-tab changes
    const onStorage = (e) => {
      if (e.key === "groupName" || e.key === "roleGroupId") {
        forceRefresh((x) => x + 1);
      }
    };

    // Same-tab signal (we will fire this after AdminSwitch)
    const onGroupSwitched = () => {
      forceRefresh((x) => x + 1);
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener("centra:group-switched", onGroupSwitched);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("centra:group-switched", onGroupSwitched);
    };
  }, []);

  return (
    <Box component="nav">
      {isSidebarOpen && (
        <Drawer
          open={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          variant={isNonMobile ? "persistent" : "temporary"}
          anchor="left"
          sx={{
            width: drawerWidth,
            "& .MuiDrawer-paper": {
              color: theme.palette.secondary[200],
              backgroundColor: theme.palette.background.alt,
              boxSizing: "border-box",
              borderWidth: isNonMobile ? 0 : "1px",
              overflowX: "hidden",
              width: drawerWidth,
            },
          }}
        >
          <Box width="100%">
            <Box m={{ xs: "1rem 1rem 1.25rem 1rem", sm: "1.5rem 2rem 2rem 2rem", md: "1.5rem 2rem 2rem 3rem" }}>
              <FlexBetween color={theme.palette.secondary.main}>
                <Box display="flex" alignItems="center" gap="0.5rem">
                  <Typography variant="h4" fontWeight="bold">
                    {groupName ? `CENTRA for ${groupName}` : "CENTRA"}
                  </Typography>
                </Box>
                {!isNonMobile && (
                  <IconButton onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
                    <ChevronLeft />
                  </IconButton>
                )}
              </FlexBetween>
            </Box>

            <List>
              {navItems
                .filter((item) => !item.sysAdminOnly || isSysAdmin)
                .map(({ text, icon, path }) => {
                  if (!icon) {
                    return (
                      <Typography key={text} sx={{ m: { xs: "1.25rem 0 0.75rem 1rem", md: "2.25rem 0 1rem 3rem" } }}>
                        {text}
                      </Typography>
                    );
                  }

                  const isActive = pathname === path;

                  return (
                    <ListItem key={text} disablePadding>
                      <ListItemButton
                        onClick={() => {
                          navigate(path);
                          setActive(path.substring(1));
                          if (!isNonMobile) setIsSidebarOpen(false);
                        }}
                        sx={{
                          backgroundColor: isActive
                            ? theme.palette.secondary[300]
                            : "transparent",
                          color: isActive
                            ? theme.palette.primary[600]
                            : theme.palette.secondary[100],
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            ml: "2rem",
                            color: isActive
                              ? theme.palette.primary[600]
                              : theme.palette.secondary[200],
                          }}
                        >
                          {icon}
                        </ListItemIcon>
                        <ListItemText primary={text} />
                        {isActive && (
                          <ChevronRightOutlined sx={{ ml: "auto" }} />
                        )}
                      </ListItemButton>
                    </ListItem>
                  );
                })}
            </List>
          </Box>

          <Box position="absolute" bottom="2rem">
            {/* ... keep your existing bottom section ... */}
          </Box>
        </Drawer>
      )}
    </Box>
  );
};

export default Sidebar;
